import React from "react";
import { createRoot } from "react-dom/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../components/ui/dialog";
import { EDSButton } from "../components/eds/EDSButton";

type ConfirmDialogOptions = {
  title: string;
  message: string;
  accept?: string;
  cancel?: string;
};

type ConfirmDialogProps = ConfirmDialogOptions & {
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmDialogComponent = ({
  title,
  message,
  accept = "Confirm",
  cancel = "Cancel",
  onConfirm,
  onCancel
}: ConfirmDialogProps) => {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-[425px]" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <EDSButton variant="outline" onClick={onCancel}>
            {cancel}
          </EDSButton>
          <EDSButton variant="default" onClick={onConfirm}>
            {accept}
          </EDSButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Display a confirmation dialog with web fallback.
 * This function provides a web-based confirmation dialog that works
 * when the native ui.displayConfirm doesn't render (e.g., in web browser mode).
 *
 * @param options - The dialog options (title, message, accept, cancel)
 * @returns Promise<boolean> - true if confirmed, false if cancelled
 */
export const displayConfirmDialog = (
  options: ConfirmDialogOptions
): Promise<boolean> => {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.id = "confirm-dialog-container";
    document.body.appendChild(container);

    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      container.remove();
    };

    const handleConfirm = () => {
      cleanup();
      resolve(true);
    };

    const handleCancel = () => {
      cleanup();
      resolve(false);
    };

    root.render(
      <ConfirmDialogComponent
        {...options}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    );
  });
};
